import { SellarDistributionAvailabilityUpcomingDeliveryListResponseType } from "@sellar-io/api-v2/types";
import { useSellarDistributionAvailabilityUpcomingDeliveryList } from "~/hooks/api/v2/sellarDistributionAvailability/useSellarDistributionAvailabilityUpcomingDeliveryList";

export type UpcomingDelivery =
  SellarDistributionAvailabilityUpcomingDeliveryListResponseType[number];

export type UpcomingDeliveryGroup = {
  key: string;
  orderCutOffDateTime: UpcomingDelivery["orderCutOffDateTime"];
  deliveryDates: Array<UpcomingDelivery["deliveryDate"]>;
  suppliers: Array<
    UpcomingDelivery["DistributionDeliveryRouteDefinition"]["DistributionScheduleAssignment"]["Supplier"]
  >;
};

const getUpcomingDeliveryGroup = (
  upcomingDeliveries: UpcomingDelivery[],
): UpcomingDeliveryGroup[] => {
  const upcomingDeliveryGroups = upcomingDeliveries.reduce<
    Map<string, UpcomingDeliveryGroup>
  >((map, entry) => {
    const key = `${entry.orderCutOffDateTime}`;
    const existing = map.get(key);
    const supplier =
      entry.DistributionDeliveryRouteDefinition.DistributionScheduleAssignment
        .Supplier;
    const deliveryDate = entry.deliveryDate;

    if (existing) {
      existing.suppliers.push(supplier);
      existing.deliveryDates.push(deliveryDate);
    } else {
      map.set(key, {
        key,
        orderCutOffDateTime: entry.orderCutOffDateTime,
        deliveryDates: [deliveryDate],
        suppliers: [supplier],
      });
    }

    return map;
  }, new Map());

  const deliveryGroupsWithUniqueSuppliers = Array.from(
    upcomingDeliveryGroups.values(),
  ).map((group) => ({
    ...group,
    suppliers: Array.from(
      new Map(
        group.suppliers.map((supplier) => [supplier.id, supplier]),
      ).values(),
    ),
  }));

  return deliveryGroupsWithUniqueSuppliers;
};

export const useGetUpcomingDeliveryGroups = ({
  buyerPostcode,
  enabled,
  arrivingByDate,
}: {
  buyerPostcode: string;
  enabled: boolean;
  arrivingByDate: {
    low: Date;
    high: Date;
  };
}) => {
  const sellarDistributionAvailabilityUpcomingDeliveryList =
    useSellarDistributionAvailabilityUpcomingDeliveryList(
      {
        query: [
          {
            type: "retailerPostcode",
            modifier: "EQUALS",
            value: buyerPostcode,
          },
          {
            type: "arrivingByDate",
            modifier: "BETWEEN",
            value: {
              low: arrivingByDate.low,
              high: arrivingByDate.high,
            },
          },
        ],
      },
      {
        enabled,
      },
    );

  const upcomingDeliveries =
    sellarDistributionAvailabilityUpcomingDeliveryList.data?.pages.flat() ?? [];

  const upcomingDeliveryGroups = getUpcomingDeliveryGroup(upcomingDeliveries);

  return {
    upcomingDeliveryGroups,
    isLoading: sellarDistributionAvailabilityUpcomingDeliveryList.isLoading,
  };
};
